import React, { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { workspaceAPI, metaPixelAPI, settingsAPI, credentialsAPI, authAPI, notifSettingsAPI } from '../lib/api';

const PERM_LABELS = {
  view: 'View tasks & projects', create: 'Create tasks & projects', edit: 'Edit tasks & projects',
  assign: 'Assign tasks to others', comment: 'Comment on tasks', change_status: 'Change task status',
  access_dashboards: 'Access dashboards & reports', manage_members: 'Manage team members',
  manage_settings: 'Manage workspace settings', delete: 'Delete tasks & pages',
};
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { PasswordInput } from '../components/PasswordInput';
import { StellarSelect } from '../components/StellarFormControls';
import { OrgChartCard } from '../components/OrgChartCard';
import { SupervisorTreeCard } from '../components/SupervisorTreeCard';
import AiReviewSettingsCard from '../components/AiReviewSettingsCard';
import SelectiveWipeButton from '../components/SelectiveWipeButton';
import WorkspaceHolidaysCard from '../components/WorkspaceHolidaysCard';
import WorkScheduleCard from '../components/WorkScheduleCard';
import NotificationPreferencesPanel from '../components/NotificationPreferencesPanel';
import VelocitySettingsPanel from '../components/VelocitySettingsPanel';
import BrandTextRulesPanel from '../components/BrandTextRulesPanel';
import ClockInLocationPolicyCard from '../components/ClockInLocationPolicyCard';
import ClockInWindowCard from '../components/ClockInWindowCard';
import EmailHealthCard from '../components/EmailHealthCard';
import {
  Save, Loader2, Plus, Building2, Share2, Bell, KeyRound, Mail, Trash2, Eye, EyeOff,
  CheckCircle2, XCircle, ExternalLink, User, Lock, Users, Shield, MoreHorizontal, UserPlus,
  Gauge, ShieldCheck
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

const PLATFORM_OPTIONS = [
  { value: 'facebook', label: 'Facebook / Instagram', helpUrl: 'https://developers.facebook.com/apps/', helpText: 'Create an app at Meta for Developers' },
  { value: 'linkedin', label: 'LinkedIn', helpUrl: 'https://www.linkedin.com/developers/apps/', helpText: 'Create an app at LinkedIn Developers' },
  { value: 'google', label: 'Google (GMB / YouTube)', helpUrl: 'https://console.cloud.google.com/apis/credentials', helpText: 'Create OAuth credentials in Google Cloud Console' },
  { value: 'pinterest', label: 'Pinterest', helpUrl: 'https://developers.pinterest.com/apps/', helpText: 'Register at Pinterest Developers' },
];

export default function SettingsPage({ hideCredentials = false }) {
  const { currentWorkspace, createWorkspace, fetchWorkspaces, workspaceRole } = useWorkspace();
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  // Iter 71 — gate Workspace + Members tabs to Admin/Owner. Non-permitted
  // roles attempting these tabs (by URL ?tab=workspace) are forced to Account.
  const isAdminOrOwner =
    user?.role === 'super_admin' ||
    workspaceRole === 'owner' ||
    workspaceRole === 'admin';
  const isOwnerOnly = user?.role === 'super_admin' || workspaceRole === 'owner';
  const requestedTab = searchParams.get('tab') || 'account';
  const sanitizedInitialTab =
    (requestedTab === 'workspace' || requestedTab === 'members') && !isAdminOrOwner
      ? 'account'
      : requestedTab;
  const [activeTab, setActiveTab] = useState(sanitizedInitialTab);

  React.useEffect(() => {
    // BUG (D48 follow-up): defer the role-guard until workspaceRole has
    // actually loaded. Otherwise the first render sees workspaceRole=undefined
    // → isAdminOrOwner=false → ?tab=workspace gets silently rewritten to
    // 'account' for Owners. Wait for either a defined workspaceRole or a
    // super_admin user before enforcing.
    if (workspaceRole === undefined && user?.role !== 'super_admin') return;
    if ((activeTab === 'workspace' || activeTab === 'members') && !isAdminOrOwner) {
      // eslint-disable-next-line no-console
      console.debug('[role-guard] settings tab blocked:', activeTab);
      toast.error('Access denied — this tab is restricted.', {
        description: 'Required role: Admin or Owner.',
        id: 'role-guard-settings-tab',
      });
      setActiveTab('account');
    }
  }, [activeTab, isAdminOrOwner, workspaceRole, user?.role]);
  const [saving, setSaving] = useState(false);
  const [wsName, setWsName] = useState('');
  const [wsDesc, setWsDesc] = useState('');
  const [wsIndustry, setWsIndustry] = useState('');
  const [metaPixelId, setMetaPixelId] = useState('');
  const [metaAccessToken, setMetaAccessToken] = useState('');
  const [showNewWs, setShowNewWs] = useState(searchParams.get('new') === 'true');
  const [newWsName, setNewWsName] = useState('');
  const [newWsDesc, setNewWsDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [pixelSaving, setPixelSaving] = useState(false);
  const [pixelSaved, setPixelSaved] = useState(false);

  // Account / Profile state
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileTimezone, setProfileTimezone] = useState(user?.timezone || 'America/Toronto');
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  // SMTP state
  const [smtpForm, setSmtpForm] = useState({ smtp_host: 'smtp.gmail.com', smtp_port: 587, smtp_email: '', smtp_password: '', notify_email: '' });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  // Platform credentials state
  const [platformCreds, setPlatformCreds] = useState([]);
  const [showPlatformForm, setShowPlatformForm] = useState(false);
  const [platForm, setPlatForm] = useState({ platform: 'facebook', app_id: '', app_secret: '', access_token: '' });
  const [platSaving, setPlatSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});

  // Notification settings
  const NOTIF_EVENTS = [
    { key: 'task_assigned', label: 'Task assigned to me', desc: 'When a task is assigned to you' },
    { key: 'deadline_approaching', label: 'Deadline approaching', desc: 'When a task deadline is within 24h' },
    { key: 'task_overdue', label: 'Task overdue', desc: 'When a task passes its due date' },
    { key: 'task_completed', label: 'Task completed', desc: 'When a task you assigned is completed' },
    { key: 'task_approved', label: 'Task approved/reviewed', desc: 'When your task is reviewed' },
    { key: 'task_in_progress', label: 'Task started', desc: 'When a task moves to in progress' },
    { key: 'new_comment', label: 'New comment', desc: 'When someone comments on your task' },
  ];
  const [notifSettings, setNotifSettings] = useState({});
  const [notifLoading, setNotifLoading] = useState(false);
  const [wsMembers, setWsMembers] = useState([]);
  const [wsPermissions, setWsPermissions] = useState({});
  const [permRole, setPermRole] = useState('admin');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ first_name: '', last_name: '', email: '', role: 'team', manager_id: '' });
  const [inviting, setInviting] = useState(false);

  useEffect(() => { setProfileName(user?.name || ''); setProfileTimezone(user?.timezone || 'America/Toronto'); }, [user?.name, user?.timezone]);

  useEffect(() => {
    if (currentWorkspace) {
      setWsName(currentWorkspace.name || '');
      setWsDesc(currentWorkspace.description || '');
      setWsIndustry(currentWorkspace.industry || '');
      setMetaPixelId(currentWorkspace.meta_pixel_id || '');
      setMetaAccessToken(currentWorkspace.meta_access_token || '');
      setPixelSaved(!!currentWorkspace.meta_pixel_id);
    }
  }, [currentWorkspace]);

  // Load credentials
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    credentialsAPI.getSmtp(currentWorkspace.id).then(res => {
      const d = res.data;
      setSmtpConfigured(d.configured);
      if (d.configured) {
        setSmtpForm(f => ({ ...f, smtp_host: d.smtp_host, smtp_port: d.smtp_port, smtp_email: d.smtp_email, notify_email: d.notify_email, smtp_password: '' }));
      }
    }).catch(() => {});
    credentialsAPI.getPlatforms(currentWorkspace.id).then(res => {
      setPlatformCreds(res.data);
    }).catch(() => {});
    notifSettingsAPI.get(currentWorkspace.id).then(res => setNotifSettings(res.data || {})).catch(() => {});
    workspaceAPI.listMembers(currentWorkspace.id).then(res => setWsMembers(res.data || [])).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/permissions/${currentWorkspace.id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('stellar_token')}` } })
      .then(r => r.json()).then(d => setWsPermissions(d.permissions || {})).catch(() => {});
  }, [currentWorkspace?.id]);

  const toggleNotifSetting = async (eventKey, channel) => {
    const current = notifSettings[eventKey]?.[channel] ?? true;
    const updated = { ...notifSettings, [eventKey]: { ...notifSettings[eventKey], [channel]: !current } };
    setNotifSettings(updated);
    try {
      await notifSettingsAPI.update(currentWorkspace.id, updated);
    } catch { toast.error('Failed to save'); }
  };

  const handleSaveWorkspace = async () => {
    setSaving(true);
    try {
      await workspaceAPI.update(currentWorkspace.id, { name: wsName, description: wsDesc, industry: wsIndustry, meta_pixel_id: metaPixelId });
      toast.success('Workspace settings saved');
      fetchWorkspaces();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSaveMetaPixel = async () => {
    if (!metaPixelId.trim()) { toast.error('Please enter a valid Meta Pixel ID'); return; }
    setPixelSaving(true);
    try {
      await workspaceAPI.update(currentWorkspace.id, { name: wsName, description: wsDesc, industry: wsIndustry, meta_pixel_id: metaPixelId });
      if (metaAccessToken.trim()) {
        await metaPixelAPI.connect(currentWorkspace.id, { pixel_id: metaPixelId, access_token: metaAccessToken });
        toast.success('Meta Pixel connected with access token');
      } else {
        toast.success('Meta Pixel ID saved');
      }
      setPixelSaved(true);
      fetchWorkspaces();
    } catch { toast.error('Failed to save Meta Pixel'); }
    finally { setPixelSaving(false); }
  };

  const handleSaveSmtp = async () => {
    if (!smtpForm.smtp_email.trim()) { toast.error('SMTP email is required'); return; }
    if (!smtpForm.smtp_password.trim() && !smtpConfigured) { toast.error('SMTP password is required'); return; }
    setSmtpSaving(true);
    try {
      await credentialsAPI.saveSmtp(currentWorkspace.id, smtpForm);
      toast.success('SMTP credentials saved');
      setSmtpConfigured(true);
      setSmtpForm(f => ({ ...f, smtp_password: '' }));
    } catch { toast.error('Failed to save SMTP credentials'); }
    finally { setSmtpSaving(false); }
  };

  const handleSavePlatform = async () => {
    if (!platForm.app_id.trim()) { toast.error('App ID is required'); return; }
    setPlatSaving(true);
    try {
      await credentialsAPI.savePlatform(currentWorkspace.id, platForm);
      toast.success(`${platForm.platform} credentials saved`);
      setShowPlatformForm(false);
      setPlatForm({ platform: 'facebook', app_id: '', app_secret: '', access_token: '' });
      const res = await credentialsAPI.getPlatforms(currentWorkspace.id);
      setPlatformCreds(res.data);
    } catch { toast.error('Failed to save credentials'); }
    finally { setPlatSaving(false); }
  };

  const handleDeletePlatform = async (platform) => {
    try {
      await credentialsAPI.deletePlatform(currentWorkspace.id, platform);
      toast.success(`${platform} credentials removed`);
      setPlatformCreds(prev => prev.filter(c => c.platform !== platform));
    } catch { toast.error('Failed to delete'); }
  };

  const handleCreateWorkspace = async () => {
    if (!newWsName.trim()) { toast.error('Name required'); return; }
    setCreating(true);
    try {
      await createWorkspace({ name: newWsName, description: newWsDesc });
      toast.success('Workspace created');
      setShowNewWs(false);
      setNewWsName('');
      setNewWsDesc('');
    } catch { toast.error('Failed to create workspace'); }
    finally { setCreating(false); }
  };

  const handleUpdateProfile = async () => {
    if (!profileName.trim()) { toast.error('Name cannot be empty'); return; }
    setProfileSaving(true);
    try {
      await authAPI.updateProfile({ name: profileName.trim(), timezone: profileTimezone });
      toast.success('Profile updated');
      if (refreshUser) refreshUser();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to update'); }
    finally { setProfileSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current) { toast.error('Enter your current password'); return; }
    if (!pwForm.newPw || pwForm.newPw.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    setPwSaving(true);
    try {
      await authAPI.changePassword({ current_password: pwForm.current, new_password: pwForm.newPw });
      toast.success('Password changed successfully');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to change password'); }
    finally { setPwSaving(false); }
  };

  return (
    <div className="p-6 space-y-6 animate-fade-in-up" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage workspace, credentials, and notifications.</p>
        </div>
        <Button variant="outline" onClick={() => setShowNewWs(true)} disabled={!isOwnerOnly} className={!isOwnerOnly ? 'hidden' : ''} data-testid="settings-new-workspace-btn">
          <Plus className="h-4 w-4 mr-2" /> New Workspace
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* D54 B6 — Mobile: horizontal scroll with snap + fade edges so the
            tab strip never wraps into the content above. Desktop ≥sm:
            preserves the wrap-friendly grid behavior. */}
        <div className="overflow-x-auto -mx-2 px-2 sm:overflow-visible sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsList className="inline-flex flex-nowrap whitespace-nowrap snap-x sm:flex-wrap sm:whitespace-normal">
            <TabsTrigger value="account" className="snap-start">
              <User className="h-4 w-4 mr-2" /> My Account
            </TabsTrigger>
            {isAdminOrOwner && (
              <TabsTrigger value="workspace" data-testid="settings-tab-workspace" className="snap-start">
                <Building2 className="h-4 w-4 mr-2" /> Workspace
              </TabsTrigger>
            )}
            {isAdminOrOwner && (
              <TabsTrigger value="members" data-testid="settings-tab-members" className="snap-start">
                <Users className="h-4 w-4 mr-2" /> Members & Permissions
              </TabsTrigger>
            )}
            <TabsTrigger value="notifications" className="snap-start">
              <Bell className="h-4 w-4 mr-2" /> Notifications
            </TabsTrigger>
            {isAdminOrOwner && (
              <TabsTrigger value="velocity" data-testid="settings-tab-velocity" className="snap-start">
                <Gauge className="h-4 w-4 mr-2" /> Velocity
              </TabsTrigger>
            )}
            {isAdminOrOwner && (
              <TabsTrigger value="brand-rules" data-testid="settings-tab-brand-rules" className="snap-start">
                <ShieldCheck className="h-4 w-4 mr-2" /> Brand Rules
              </TabsTrigger>
            )}
            {/* D64 — Owner-only Email Health tab. */}
            {isOwnerOnly && (
              <TabsTrigger value="email" data-testid="settings-tab-email" className="snap-start">
                <Mail className="h-4 w-4 mr-2" /> Email
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* ──── My Account Tab ──── */}
        <TabsContent value="account" className="space-y-4 mt-4">
          <Card className="bg-card/60 backdrop-blur" data-testid="profile-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <User className="h-4 w-4 text-gold" /> Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 mb-2">
                <div className="h-14 w-14 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-lg text-primary font-semibold">
                    {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  {/*
                    Phase 2 Group C1 — display the user's WORKSPACE role, not
                    their global users.role. Srushti's global role is 'admin'
                    (legacy) but in this workspace she's Team — and her server-
                    side permissions are correctly Team. Only the badge label
                    was misleading. super_admin globally still beats workspace.
                  */}
                  {(() => {
                    const ROLE_LABELS = {
                      owner: 'Owner',
                      admin: 'Admin',
                      manager: 'Manager',
                      team: 'Team',
                      member: 'Team',
                      editor: 'Team',
                      viewer: 'Team',
                      super_admin: 'Owner',
                    };
                    const effective = user?.role === 'super_admin' ? 'super_admin' : (workspaceRole || user?.role || 'team');
                    const label = ROLE_LABELS[effective] || 'Team';
                    return <Badge className="mt-1 text-[10px]" data-testid="my-account-role-badge">{label}</Badge>;
                  })()}
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <Label htmlFor="profile-name">Display Name</Label>
                <Input id="profile-name" value={profileName} onChange={e => setProfileName(e.target.value)}
                  placeholder="Your full name" data-testid="profile-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email || ''} disabled className="opacity-60" />
                <p className="text-[11px] text-muted-foreground">Email cannot be changed. Contact admin for assistance.</p>
              </div>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <p className="text-[11px] text-muted-foreground mb-1">Auto-detected from your browser. Change manually if needed.</p>
                <Select value={profileTimezone} onValueChange={async (newTz) => {
                  setProfileTimezone(newTz);
                  try { await authAPI.updateProfile({ timezone: newTz }); if (refreshUser) refreshUser(); toast.success(`Timezone: ${newTz}`); } catch { toast.error('Failed'); }
                }}>
                  <SelectTrigger className="h-9" data-testid="timezone-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="America/St_Johns">Newfoundland (UTC-3:30)</SelectItem>
                    <SelectItem value="America/Halifax">Atlantic - Halifax (UTC-4)</SelectItem>
                    <SelectItem value="America/Toronto">Eastern - Toronto (UTC-5)</SelectItem>
                    <SelectItem value="America/Winnipeg">Central - Winnipeg (UTC-6)</SelectItem>
                    <SelectItem value="America/Regina">Saskatchewan - Regina (UTC-6)</SelectItem>
                    <SelectItem value="America/Edmonton">Mountain - Edmonton (UTC-7)</SelectItem>
                    <SelectItem value="America/Vancouver">Pacific - Vancouver (UTC-8)</SelectItem>
                    <SelectItem value="Asia/Kolkata">India Standard Time (UTC+5:30)</SelectItem>
                    <SelectItem value="America/New_York">Eastern - New York (UTC-5)</SelectItem>
                    <SelectItem value="America/Chicago">Central - Chicago (UTC-6)</SelectItem>
                    <SelectItem value="America/Denver">Mountain - Denver (UTC-7)</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific - LA (UTC-8)</SelectItem>
                    <SelectItem value="Europe/London">London (UTC+0)</SelectItem>
                    <SelectItem value="Asia/Dubai">Dubai (UTC+4)</SelectItem>
                    <SelectItem value="Asia/Singapore">Singapore (UTC+8)</SelectItem>
                    <SelectItem value="Australia/Sydney">Sydney (UTC+10)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleUpdateProfile} disabled={profileSaving || profileName === user?.name} data-testid="save-profile-button">
                {profileSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Profile
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-card/60 backdrop-blur" data-testid="change-password-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Lock className="h-4 w-4 text-gold" /> Change Password
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-pw">Current Password</Label>
                <PasswordInput id="current-pw" value={pwForm.current}
                  onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                  placeholder="Enter current password" data-testid="current-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-pw">New Password</Label>
                <PasswordInput id="new-pw" value={pwForm.newPw}
                  onChange={e => setPwForm(f => ({ ...f, newPw: e.target.value }))}
                  placeholder="Min 6 characters" data-testid="new-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-pw">Confirm New Password</Label>
                <PasswordInput id="confirm-pw" value={pwForm.confirm}
                  onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                  placeholder="Re-enter new password" data-testid="confirm-password-input" />
              </div>
              <Button onClick={handleChangePassword} disabled={pwSaving || !pwForm.current || !pwForm.newPw} data-testid="change-password-button">
                {pwSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                Change Password
              </Button>
            </CardContent>
          </Card>

          <WorkScheduleCard />
        </TabsContent>

        {/* Workspace Tab */}
        <TabsContent value="workspace" className="space-y-4 mt-4">
          <Card className="bg-card/60 backdrop-blur">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Workspace Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Workspace Name</Label>
                <Input value={wsName} onChange={e => setWsName(e.target.value)} placeholder="My Agency" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={wsDesc} onChange={e => setWsDesc(e.target.value)} placeholder="Social media management for..." />
              </div>
              <div className="space-y-2">
                <Label>Industry</Label>
                <Input value={wsIndustry} onChange={e => setWsIndustry(e.target.value)} placeholder="Restaurant, Fashion, Tech..." />
              </div>
              <Separator />
              <Button onClick={handleSaveWorkspace} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </CardContent>
          </Card>

          <AiReviewSettingsCard workspaceId={currentWorkspace?.id} role={user?.role} />

          <WorkspaceHolidaysCard workspaceId={currentWorkspace?.id} role={user?.role} />

          {isAdminOrOwner && <ClockInLocationPolicyCard workspaceId={currentWorkspace?.id} />}
          <ClockInWindowCard workspaceId={currentWorkspace?.id} />

          <SelectiveWipeButton role={user?.role} />
        </TabsContent>

        {/* ──── API Credentials Tab ──── */}
        <TabsContent value="credentials" className="space-y-4 mt-4">
          {/* SMTP Section */}
          <Card className="bg-card/60 backdrop-blur" data-testid="smtp-credentials-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gold" /> Email / SMTP
                </CardTitle>
                {smtpConfigured ? (
                  <Badge className="bg-emerald-500/15 text-emerald-500 border border-emerald-500/20">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
                  </Badge>
                ) : (
                  <Badge className="bg-secondary text-muted-foreground">
                    <XCircle className="h-3 w-3 mr-1" /> Not configured
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Configure SMTP to send email invitations and notifications. For Gmail, use an{' '}
                <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-gold underline underline-offset-2">
                  App Password
                </a>{' '}
                (not your regular password).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SMTP Host</Label>
                  <Input value={smtpForm.smtp_host} onChange={e => setSmtpForm(f => ({ ...f, smtp_host: e.target.value }))} placeholder="smtp.gmail.com" data-testid="smtp-host-input" />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Port</Label>
                  <Input type="number" value={smtpForm.smtp_port} onChange={e => setSmtpForm(f => ({ ...f, smtp_port: parseInt(e.target.value) || 587 }))} placeholder="587" data-testid="smtp-port-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input value={smtpForm.smtp_email} onChange={e => setSmtpForm(f => ({ ...f, smtp_email: e.target.value }))} placeholder="you@gmail.com" data-testid="smtp-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>Password / App Password</Label>
                  <div className="relative">
                    <Input
                      type={showSmtpPassword ? 'text' : 'password'}
                      value={smtpForm.smtp_password}
                      onChange={e => setSmtpForm(f => ({ ...f, smtp_password: e.target.value }))}
                      placeholder={smtpConfigured ? '(unchanged — enter new to update)' : 'App password'}
                      data-testid="smtp-password-input"
                    />
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowSmtpPassword(!showSmtpPassword)}>
                      {showSmtpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notification Recipient Email (optional)</Label>
                <Input value={smtpForm.notify_email} onChange={e => setSmtpForm(f => ({ ...f, notify_email: e.target.value }))} placeholder="Same as sender if left empty" data-testid="smtp-notify-input" />
                <p className="text-[11px] text-muted-foreground">Receives alerts for new signups, team joins, etc.</p>
              </div>
              <Button onClick={handleSaveSmtp} disabled={smtpSaving} data-testid="smtp-save-button">
                {smtpSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {smtpConfigured ? 'Update SMTP' : 'Save SMTP'}
              </Button>
            </CardContent>
          </Card>

          {/* Social Platform Credentials */}
          <Card className="bg-card/60 backdrop-blur" data-testid="platform-credentials-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-gold" /> Social Platform API Keys
                </CardTitle>
                <Button size="sm" onClick={() => { setPlatForm({ platform: 'facebook', app_id: '', app_secret: '', access_token: '' }); setShowPlatformForm(true); }} data-testid="add-platform-credentials-button">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Platform
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Add your developer API credentials for each social media platform you want to connect. These are used for posting content and pulling analytics.
              </p>

              {platformCreds.length === 0 ? (
                <div className="rounded-lg bg-secondary/30 p-6 text-center">
                  <KeyRound className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm font-medium">No platform credentials saved</p>
                  <p className="text-xs text-muted-foreground mt-1">Add your Facebook, LinkedIn, or other API keys to enable posting.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {platformCreds.map(cred => {
                    const plat = PLATFORM_OPTIONS.find(p => p.value === cred.platform);
                    return (
                      <div key={cred.platform} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30" data-testid={`platform-cred-${cred.platform}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <Badge className="bg-primary/15 text-primary border border-primary/20 capitalize">{plat?.label || cred.platform}</Badge>
                          <div className="text-xs min-w-0">
                            <p className="font-medium">App ID: {cred.app_id}</p>
                            <p className="text-muted-foreground">
                              Secret: {cred.app_secret_set ? (showSecrets[cred.platform] ? cred.app_secret : '••••••••') : 'Not set'}
                              {cred.access_token_set && ` | Token: ${showSecrets[cred.platform] ? cred.access_token : '••••••••'}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowSecrets(s => ({ ...s, [cred.platform]: !s[cred.platform] }))}>
                            {showSecrets[cred.platform] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setPlatForm({ platform: cred.platform, app_id: cred.app_id, app_secret: '', access_token: '' }); setShowPlatformForm(true); }}>
                            <Save className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeletePlatform(cred.platform)} data-testid={`delete-cred-${cred.platform}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Meta Pixel Tab */}
        <TabsContent value="integrations" className="space-y-4 mt-4">
          <Card className="bg-card/60 backdrop-blur">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Meta Pixel Configuration</CardTitle>
                {pixelSaved && metaPixelId && (
                  <Badge data-testid="meta-pixel-status-badge" className="bg-emerald-500/15 text-emerald-500 border border-emerald-500/20">Connected</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">Connect your Meta Pixel to track conversions and pull analytics data.</p>
              <div className="space-y-2">
                <Label>Meta Pixel ID</Label>
                <Input value={metaPixelId} onChange={e => { setMetaPixelId(e.target.value); setPixelSaved(false); }} placeholder="123456789012345" data-testid="meta-pixel-id-input" />
              </div>
              <div className="space-y-2">
                <Label>Meta Access Token</Label>
                <Input value={metaAccessToken} onChange={e => { setMetaAccessToken(e.target.value); setPixelSaved(false); }} placeholder="EAABsb..." type="password" data-testid="meta-access-token-input" />
                <p className="text-xs text-muted-foreground">From Meta Events Manager or Meta for Developers.</p>
              </div>
              <Button onClick={handleSaveMetaPixel} disabled={pixelSaving} data-testid="meta-pixel-save-button">
                {pixelSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Pixel ID
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}

        {/* ──── Members & Permissions Tab ──── */}
        <TabsContent value="members" className="space-y-6 mt-4">
          {/* Members List */}
          <Card className="bg-card/60 backdrop-blur">
            <CardHeader className="pb-3 flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-gold" /> Team Members</CardTitle>
              <div className="flex items-center gap-2">
                {(user?.role === 'owner' || user?.role === 'super_admin') && (
                  <Button size="sm" variant="outline" className="h-8 text-[11px]" data-testid="test-smtp-btn" onClick={async () => {
                    // D55 PART A — Quick SMTP health check from prod for the Owner.
                    const to = prompt('Send a test email to which address?', user.email || '');
                    if (!to) return;
                    try {
                      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/test-email?to=${encodeURIComponent(to)}`, { method:'POST', headers:{'Authorization':`Bearer ${localStorage.getItem('stellar_token')}`} });
                      const j = await r.json();
                      if (j.ok) {
                        toast.success(`SMTP test email sent to ${to}. Check the inbox in <60s.`);
                      } else {
                        const detail = `${j.reason || 'unknown'}: ${j.error || ''}`;
                        toast.error(`SMTP test failed → ${detail}`, { duration: 16000 });
                        console.warn('[test-smtp]', j);
                      }
                    } catch (e) {
                      toast.error('SMTP test endpoint unreachable');
                    }
                  }}>Test SMTP</Button>
                )}
                {/* D64 P2-8 — Invite Member restricted to Owner-tier only.
                    Admins view members as read-only. */}
                {['super_admin','owner'].includes(user?.role) && <Button size="sm" className="h-8 text-[12px] bg-gold/10 hover:bg-gold/20 text-gold border-0" data-testid="invite-member-btn" onClick={() => setShowInviteModal(true)}><UserPlus className="h-3.5 w-3.5 mr-1.5" /> Invite Member</Button>}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {wsMembers.map(m => {
                  const u2 = m.user || {};
                  const isPending = u2.account_status === 'invited';
                  const canManage = ['super_admin','owner','admin'].includes(user?.role);
                  return (
                    <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-foreground/[0.02]">
                      <div className="h-8 w-8 rounded-full bg-gold/10 flex items-center justify-center shrink-0"><span className="text-[11px] font-semibold text-gold">{(u2.name||'?')[0]?.toUpperCase()}</span></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium">{u2.name || u2.email}</p>
                        <p className="text-[11px] text-muted-foreground/40">{u2.email}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] border-border/30">{m.role}</Badge>
                      {isPending && <Badge className="text-[9px] bg-amber-500/10 text-amber-400 border-amber-500/20">Pending</Badge>}
                      <span className="text-[10px] text-muted-foreground/30">{m.joined_at?.slice(0,10)}</span>
                      {canManage && m.role !== 'owner' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><button className="p-1 rounded hover:bg-foreground/[0.05]"><MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground/40" /></button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            {['admin','manager','team'].filter(r => r !== m.role).map(r => (
                              <DropdownMenuItem key={r} className="text-[12px]" onClick={async () => {
                                try { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${currentWorkspace.id}/members/${m.id}/role`, { method:'PUT', headers:{'Authorization':`Bearer ${localStorage.getItem('stellar_token')}`,'Content-Type':'application/json'}, body:JSON.stringify({role:r}) }); toast.success(`Role changed to ${r}`); workspaceAPI.listMembers(currentWorkspace.id).then(res => setWsMembers(res.data||[])); } catch { toast.error('Failed'); }
                              }}>Change to {r.charAt(0).toUpperCase()+r.slice(1)}</DropdownMenuItem>
                            ))}
                            {isPending && <DropdownMenuItem data-testid="resend-invitation-item" className="text-[12px]" onClick={async () => { try { const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${currentWorkspace.id}/members/${m.id}/resend-invite`, { method:'POST', headers:{'Authorization':`Bearer ${localStorage.getItem('stellar_token')}`} }); if (!r.ok) throw new Error(`${r.status}`); const j = await r.json(); if (j.email_sent) { toast.success(`Invitation resent to ${j.email || u2.email}`); } else { try { await navigator.clipboard.writeText(j.invite_url || ''); toast.warning(`Email failed (${j.email_reason || 'unknown'}). Invite link copied to clipboard — share it manually.`, { duration: 12000 }); } catch { toast.warning(`Email failed (${j.email_reason || 'unknown'}). Invite link: ${j.invite_url}`, { duration: 12000 }); } } } catch (e) { toast.error('Failed to resend invitation'); } }}>Resend Invitation</DropdownMenuItem>}
                            {isPending && <DropdownMenuItem data-testid="copy-invite-link-item" className="text-[12px]" onClick={async () => { try { const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${currentWorkspace.id}/members/${m.id}/invite-link`, { headers:{'Authorization':`Bearer ${localStorage.getItem('stellar_token')}`} }); if (!r.ok) { const err = await r.json().catch(()=>({})); throw new Error(err.detail || `${r.status}`); } const j = await r.json(); try { await navigator.clipboard.writeText(j.invite_url || ''); toast.success('Invite link copied to clipboard'); } catch { toast.success(`Invite link: ${j.invite_url}`); } } catch (e) { toast.error(typeof e?.message === 'string' ? e.message : 'Failed to copy invite link'); } }}>Copy Invite Link</DropdownMenuItem>}
                            {isPending && <DropdownMenuItem className="text-destructive text-[12px]" onClick={async () => { try { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${currentWorkspace.id}/members/${m.id}`, { method:'DELETE', headers:{'Authorization':`Bearer ${localStorage.getItem('stellar_token')}`} }); toast.success('Invitation revoked'); setWsMembers(prev => prev.filter(x => x.id !== m.id)); } catch { toast.error('Failed'); } }}>Revoke Invitation</DropdownMenuItem>}
                            {!isPending && <DropdownMenuItem className="text-destructive text-[12px]" onClick={async () => { if(!confirm(`Remove ${u2.name||u2.email}? Their tasks will be reassigned.`)) return; try { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${currentWorkspace.id}/members/${m.id}`, { method:'DELETE', headers:{'Authorization':`Bearer ${localStorage.getItem('stellar_token')}`} }); toast.success('Member removed'); setWsMembers(prev => prev.filter(x => x.id !== m.id)); } catch { toast.error('Failed'); } }}>Remove Member</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Org Chart — visible to everyone, editable by Owner/Admin only (Iter 52) */}
          <OrgChartCard workspaceId={currentWorkspace?.id} viewerRole={user?.role} />

          {/* Batch 5 D4-UI-1 — Supervisor Tree (transitive, multi-supervisor). */}
          <SupervisorTreeCard workspaceId={currentWorkspace?.id} viewerRole={workspaceRole || user?.role} />

          {/* Role Permissions Grid — Only visible to Owner and Admin */}
          {['super_admin','owner','admin'].includes(user?.role) && (
          <Card className="bg-card/60 backdrop-blur">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Shield className="h-4 w-4 text-gold" /> Role Permissions — what each role can do</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                {['admin','manager','team'].map(r => (
                  <button key={r} onClick={() => setPermRole(r)} className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${permRole === r ? 'bg-gold/15 text-gold' : 'text-muted-foreground/40 hover:text-muted-foreground/60'}`}>{r.charAt(0).toUpperCase()+r.slice(1)}</button>
                ))}
              </div>
              <div className="space-y-2">
                {Object.entries(PERM_LABELS).map(([key, label]) => {
                  const isOn = wsPermissions[permRole]?.[key] ?? true;
                  return (
                    <div key={key} className="flex items-center justify-between py-2 px-2 rounded hover:bg-foreground/[0.01]">
                      <span className="text-[13px]">{label}</span>
                      <Switch checked={isOn} onCheckedChange={async (val) => {
                        const updated = { ...wsPermissions, [permRole]: { ...wsPermissions[permRole], [key]: val } };
                        setWsPermissions(updated);
                        try { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/permissions/${currentWorkspace.id}`, { method:'PUT', headers:{'Authorization':`Bearer ${localStorage.getItem('stellar_token')}`,'Content-Type':'application/json'}, body:JSON.stringify({role:permRole,permissions:updated[permRole]}) }); } catch { toast.error('Failed to save'); }
                      }} />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          )}

          {/* Invite Modal */}
          {showInviteModal && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowInviteModal(false)}>
              <div className="bg-card border border-border/40 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                <h3 className="text-[17px] font-display font-bold mb-1">Invite New Member</h3>
                <p className="text-[12px] text-muted-foreground/50 mb-4">They'll receive an email with a secure invite link to set their password.</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[11px]">First Name *</Label><Input value={inviteData.first_name} onChange={e => setInviteData(p=>({...p,first_name:e.target.value}))} className="h-9 text-[13px] bg-foreground/[0.02] border-border/30" /></div>
                    <div><Label className="text-[11px]">Last Name</Label><Input value={inviteData.last_name} onChange={e => setInviteData(p=>({...p,last_name:e.target.value}))} className="h-9 text-[13px] bg-foreground/[0.02] border-border/30" /></div>
                  </div>
                  <div><Label className="text-[11px]">Email *</Label><Input value={inviteData.email} onChange={e => setInviteData(p=>({...p,email:e.target.value}))} className="h-9 text-[13px] bg-foreground/[0.02] border-border/30" placeholder="name@company.com" /></div>
                  <div><Label className="text-[11px]">Role</Label>
                    <StellarSelect
                      value={inviteData.role}
                      onValueChange={(v) => setInviteData(p=>({...p, role: v, manager_id: ''}))}
                      options={[
                        { value: 'admin', label: 'Admin' },
                        { value: 'manager', label: 'Manager' },
                        { value: 'team', label: 'Team' },
                      ]}
                      testId="invite-role-select"
                    />
                  </div>
                  <div><Label className="text-[11px]">Reports to <span className="text-muted-foreground/40">(optional)</span></Label>
                    {inviteData.role === 'owner' ? (
                      <p className="text-[11px] text-muted-foreground/40 italic h-9 flex items-center" data-testid="invite-manager-owner-hint">Owner has no manager.</p>
                    ) : (
                    <StellarSelect
                      value={inviteData.manager_id || '__none__'}
                      onValueChange={(v) => setInviteData(p=>({...p, manager_id: v === '__none__' ? '' : v}))}
                      options={(() => {
                        // 2026-05-25 fix: Team can report to Manager, Admin, or Owner.
                        // Manager can report to Admin or Owner. Admin reports to Owner.
                        // Matches backend `_allowed_manager_roles_for` exactly so the
                        // dropdown can never offer an option the server will reject.
                        const allowedByRole = { team: ['manager', 'admin', 'owner'], manager: ['admin', 'owner'], admin: ['owner'] };
                        const allowed = allowedByRole[inviteData.role] || [];
                        // Render candidates grouped by role tier (owner first, then admin,
                        // then manager) so the picker reads top-down per spec.
                        const tierOrder = { owner: 0, admin: 1, manager: 2 };
                        const candidates = wsMembers
                          .filter(m => allowed.includes(m.role))
                          .sort((a, b) => (tierOrder[a.role] ?? 9) - (tierOrder[b.role] ?? 9) || (a.user?.name || '').localeCompare(b.user?.name || ''));
                        return [
                          { value: '__none__', label: 'Not set (Owner can assign later)' },
                          ...candidates.map(m => ({ value: m.id, label: `${m.user?.name || m.user?.email} (${m.role})` })),
                        ];
                      })()}
                      testId="invite-manager-select"
                    />
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => setShowInviteModal(false)}>Cancel</Button>
                  <Button size="sm" className="bg-gold/10 hover:bg-gold/20 text-gold border-0" disabled={inviting} onClick={async () => {
                    if (!inviteData.first_name || !inviteData.email) { toast.error('First name and email required'); return; }
                    setInviting(true);
                    try {
                      const res = await workspaceAPI.inviteMember(currentWorkspace.id, { ...inviteData, manager_id: inviteData.manager_id || null });
                      const d = res?.data || {};
                      // D55 PART A — Surface delivery status. Never report success when email failed.
                      if (d.email_sent) {
                        toast.success(`Invite sent to ${inviteData.email}`);
                      } else {
                        const reason = d.email_reason || 'unknown';
                        const link = d.invite_url || '';
                        try { await navigator.clipboard.writeText(link); } catch { /* no clipboard access */ }
                        toast.warning(
                          `Member created but email failed (${reason}). Invite link copied to clipboard — share manually.`,
                          { duration: 14000 },
                        );
                        // Also log the raw SMTP error to console so the owner can debug.
                        if (d.email_error) console.warn('[invite] email_error:', d.email_error);
                      }
                      setShowInviteModal(false);
                      setInviteData({first_name:'',last_name:'',email:'',role:'team',manager_id:''});
                      workspaceAPI.listMembers(currentWorkspace.id).then(r => setWsMembers(r.data||[]));
                    } catch(err) {
                      toast.error(err?.response?.data?.detail || 'Failed');
                    }
                    setInviting(false);
                  }} data-testid="invite-send-btn">{inviting ? 'Sending...' : 'Send Invite'}</Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>


        <TabsContent value="notifications" className="space-y-4 mt-4">
          <NotificationPreferencesPanel
            workspaceId={currentWorkspace?.id}
            highlightType={searchParams.get('highlight') || ''}
          />
        </TabsContent>

        {/* ──── F3 Velocity Engine — Workspace velocity insights ──── */}
        <TabsContent value="velocity" className="space-y-4 mt-4">
          <VelocitySettingsPanel workspaceId={currentWorkspace?.id} />
        </TabsContent>

        {/* ──── D50.1 — Brand Text Rules (D51 AI video review consumes these) ──── */}
        <TabsContent value="brand-rules" className="space-y-4 mt-4">
          <BrandTextRulesPanel workspaceId={currentWorkspace?.id} />
        </TabsContent>

        {/* ──── D64 — Email Health (Owner-only) ──── */}
        {isOwnerOnly && (
          <TabsContent value="email" className="space-y-4 mt-4">
            <EmailHealthCard defaultTestEmail={user?.email || ''} />
          </TabsContent>
        )}
      </Tabs>

      {/* New Workspace Dialog — Phase 3B Batch 1 T-CRIT-1: only show for Owner/super_admin.
          Team/Manager/Admin URL-typing `?new=true` are blocked here AND server-side. */}
      <Dialog open={showNewWs && isOwnerOnly} onOpenChange={setShowNewWs}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Workspace Name</Label>
              <Input value={newWsName} onChange={e => setNewWsName(e.target.value)} placeholder="Da India Curry House" />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input value={newWsDesc} onChange={e => setNewWsDesc(e.target.value)} placeholder="Social media management for..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewWs(false)}>Cancel</Button>
            <Button onClick={handleCreateWorkspace} disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Platform Credentials Dialog */}
      <Dialog open={showPlatformForm} onOpenChange={setShowPlatformForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Platform Credentials</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={platForm.platform} onValueChange={v => setPlatForm(f => ({ ...f, platform: v }))}>
                <SelectTrigger data-testid="platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORM_OPTIONS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const plat = PLATFORM_OPTIONS.find(p => p.value === platForm.platform);
                return plat ? (
                  <a href={plat.helpUrl} target="_blank" rel="noreferrer" className="text-[11px] text-gold flex items-center gap-1 hover:underline">
                    <ExternalLink className="h-3 w-3" /> {plat.helpText}
                  </a>
                ) : null;
              })()}
            </div>
            <div className="space-y-2">
              <Label>App ID / Client ID</Label>
              <Input value={platForm.app_id} onChange={e => setPlatForm(f => ({ ...f, app_id: e.target.value }))} placeholder="123456789..." data-testid="platform-app-id-input" />
            </div>
            <div className="space-y-2">
              <Label>App Secret / Client Secret</Label>
              <Input type="password" value={platForm.app_secret} onChange={e => setPlatForm(f => ({ ...f, app_secret: e.target.value }))} placeholder="Enter secret" data-testid="platform-app-secret-input" />
            </div>
            <div className="space-y-2">
              <Label>Access Token (optional)</Label>
              <Input type="password" value={platForm.access_token} onChange={e => setPlatForm(f => ({ ...f, access_token: e.target.value }))} placeholder="Long-lived token" data-testid="platform-access-token-input" />
              <p className="text-[11px] text-muted-foreground">Some platforms require an access token in addition to app credentials.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlatformForm(false)}>Cancel</Button>
            <Button onClick={handleSavePlatform} disabled={platSaving} data-testid="save-platform-credentials-button">
              {platSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
